import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import KPIPhase1 from '@/pages/KPIPhase1';
import Finance from '@/pages/Finance';
import Chauffeurs from '@/pages/Chauffeurs';
import Vehicules from '@/pages/Vehicules';
import Proprietaires from '@/pages/Proprietaires';
import ProprietairesImport from '@/pages/ProprietairesImport';
import Societes from '@/pages/Societes';
import Operations from '@/pages/Operations';
import Incidents from '@/pages/Incidents';
import Alerts from '@/pages/Alerts';
import Sauvegardes from '@/pages/SauvegardesEnhanced';
import Import from '@/pages/Import';
import Users from '@/pages/Users';
import AuditLogs from '@/pages/AuditLogs';
import SystemLogs from '@/pages/SystemLogs';
import RBACManagement from '@/pages/RBACManagement';
import CopilotIA from '@/pages/CopilotIA';
import AnalyseChauffeurs from '@/pages/AnalyseChauffeurs';
import SettingsAPI from '@/pages/SettingsAPI';
import SimulationOperateur from '@/pages/SimulationOperateur';
import AdminPricing from '@/pages/AdminPricing';
import AdminAPIKeysGeo from '@/pages/AdminAPIKeysGeo';
import Settings from '@/pages/Settings';
import IntegrationAPIKeys from '@/pages/IntegrationAPIKeys';
import NotificationSettings from '@/pages/NotificationSettings';
import AccessRequests from '@/pages/AccessRequests';
import Layout from '@/components/Layout';
import { Toaster } from '@/components/ui/sonner';
import { SecureStorage } from '@/lib/sessionManager';
import { PermissionsProvider } from '@/contexts/PermissionsContext';
import { ProtectedRoute } from '@/components/rbac';


function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = SecureStorage.getToken();
    const userData = SecureStorage.getUser();
    if (token && userData) {
      setUser(userData);
    }
    setLoading(false);
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
    </div>;
  }

  return (
    <PermissionsProvider user={user}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={!user ? <Login setUser={setUser} /> : <Navigate to="/" />} />
            
            <Route element={user ? <Layout user={user} setUser={setUser} /> : <Navigate to="/login" />}>
              <Route path="/" element={<Dashboard />} />
              
              <Route path="/kpi-phase1" element={
                <ProtectedRoute resource="kpi" action="read">
                  <KPIPhase1 />
                </ProtectedRoute>
              } />
              
              <Route path="/finance" element={
                <ProtectedRoute resource="finance" action="read">
                  <Finance />
                </ProtectedRoute>
              } />
              
              <Route path="/chauffeurs" element={
                <ProtectedRoute resource="chauffeurs" action="read">
                  <Chauffeurs />
                </ProtectedRoute>
              } />
              
              <Route path="/vehicules" element={
                <ProtectedRoute resource="vehicules" action="read">
                  <Vehicules />
                </ProtectedRoute>
              } />
              
              <Route path="/proprietaires" element={
                <ProtectedRoute resource="proprietaires" action="read">
                  <Proprietaires />
                </ProtectedRoute>
              } />
              
              <Route path="/proprietaires/import" element={
                <ProtectedRoute resource="proprietaires" action="import">
                  <ProprietairesImport />
                </ProtectedRoute>
              } />
              
              <Route path="/societes" element={
                <ProtectedRoute resource="societes" action="read">
                  <Societes />
                </ProtectedRoute>
              } />
              
              <Route path="/operations" element={
                <ProtectedRoute resource="operations" action="read">
                  <Operations />
                </ProtectedRoute>
              } />
              
              <Route path="/incidents" element={
                <ProtectedRoute resource="incidents" action="read">
                  <Incidents />
                </ProtectedRoute>
              } />
              
              <Route path="/alerts" element={
                <ProtectedRoute resource="alerts" action="read">
                  <Alerts />
                </ProtectedRoute>
              } />
              
              <Route path="/sauvegardes" element={
                <ProtectedRoute resource="backups" action="read">
                  <Sauvegardes />
                </ProtectedRoute>
              } />
              
              <Route path="/import" element={
                <ProtectedRoute resource="import" action="import">
                  <Import />
                </ProtectedRoute>
              } />
              
              <Route path="/users" element={
                <ProtectedRoute resource="users" action="read">
                  <Users />
                </ProtectedRoute>
              } />
              
              <Route path="/audit-logs" element={
                <ProtectedRoute resource="audit_logs" action="read">
                  <AuditLogs />
                </ProtectedRoute>
              } />
              
              <Route path="/system-logs" element={
                <ProtectedRoute resource="audit_logs" action="read">
                  <SystemLogs />
                </ProtectedRoute>
              } />
              
              <Route path="/rbac-management" element={
                <ProtectedRoute resource="rbac" action="view_roles">
                  <RBACManagement />
                </ProtectedRoute>
              } />
              
              <Route path="/copilot-ia" element={<CopilotIA />} />
              
              <Route path="/analyse-chauffeurs" element={<AnalyseChauffeurs />} />
              
              <Route path="/simulation-operateur" element={<SimulationOperateur />} />
              
              {/* Page Paramètres Unifiée */}
              <Route path="/settings" element={
                <ProtectedRoute resource="settings" action="read">
                  <Settings />
                </ProtectedRoute>
              } />
              
              {/* Anciennes routes settings (redirection vers /settings) */}
              <Route path="/settings/api-keys" element={
                <ProtectedRoute resource="settings" action="read">
                  <SettingsAPI />
                </ProtectedRoute>
              } />
              
              <Route path="/settings/pricing" element={
                <ProtectedRoute resource="settings" action="read">
                  <AdminPricing />
                </ProtectedRoute>
              } />
              
              <Route path="/settings/geo-api-keys" element={
                <ProtectedRoute resource="settings" action="read">
                  <AdminAPIKeysGeo />
                </ProtectedRoute>
              } />
              
              {/* Clés API d'Intégration */}
              <Route path="/settings/integration-api-keys" element={
                <ProtectedRoute resource="settings" action="read">
                  <IntegrationAPIKeys />
                </ProtectedRoute>
              } />
              
              {/* Notifications & Rapports */}
              <Route path="/settings/notifications" element={
                <ProtectedRoute resource="settings" action="read">
                  <NotificationSettings />
                </ProtectedRoute>
              } />
              
              {/* Demandes d'acces Microsoft OAuth */}
              <Route path="/access-requests" element={
                <ProtectedRoute resource="users" action="read">
                  <AccessRequests />
                </ProtectedRoute>
              } />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </PermissionsProvider>
  );
}

export default App;
